import React, { useEffect, useMemo, useState } from 'react';
import { getFieldDefinitions } from '../services/api.service';

const DEFAULT_LANGS = ['fr', 'en', 'de'];
const NO_SUBDOMAIN_KEY = '__NO_SUBDOMAIN__';

const getSubdomainValue = (subdomain) =>
    subdomain?.subdomain || subdomain?.key || subdomain?.slug || subdomain?._id || '';
const getSubdomainLabel = (subdomain) =>
    subdomain?.fullName || subdomain?.subdomain || subdomain?.name || subdomain?._id || '(Sans nom)';
const I18nEmpty = (langs = DEFAULT_LANGS) => langs.reduce((a, l) => ({ ...a, [l]: '' }), {});
const ensureI18n = (obj, langs = DEFAULT_LANGS) => ({ ...I18nEmpty(langs), ...(obj || {}) });
const pickI18n = (obj, lang, fallbacks = DEFAULT_LANGS) =>
    obj?.[lang] || fallbacks.map((l) => obj?.[l]).find(Boolean) || '';

const byCategory = (arr) =>
    arr.reduce((acc, f) => {
        const cat = f.category || 'general';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(f);
        return acc;
    }, {});

const hasConditionalDisplay = (field) => Boolean(field?.conditionalDisplay?.fieldKey);

export default function FieldSelector({
    value = [],
    onChange,
    lang = 'fr',
    langs = DEFAULT_LANGS,
    disabled = false,
    catalog: catalogProp,
    subdomains = [],
}) {
    const [q, setQ] = useState('');
    const [catalog, setCatalog] = useState(catalogProp || []);

    useEffect(() => {
        setCatalog(catalogProp || []);
    }, [catalogProp]);

    useEffect(() => {
        if (catalogProp && catalogProp.length) return;

        (async () => {
            try {
                const list = await getFieldDefinitions();
                setCatalog(list.filter((f) => f.enabled));
            } catch {
                /* swallow */
            }
        })();
    }, [catalogProp]);

    const selectedMap = useMemo(() => {
        const m = new Map();
        value.forEach((f) => m.set(f.key, f));
        return m;
    }, [value]);

    const selectedCatalogMap = useMemo(() => {
        const m = new Map();
        catalog.forEach((f) => m.set(f.key, f));
        return m;
    }, [catalog]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return catalog;

        return catalog.filter(
            (f) =>
                (f.key || '').toLowerCase().includes(s) ||
                pickI18n(f.labels, lang).toLowerCase().includes(s) ||
                pickI18n(f.descriptions, lang).toLowerCase().includes(s) ||
                (f.category || '').toLowerCase().includes(s)
        );
    }, [q, catalog, lang]);

    const grouped = useMemo(() => byCategory(filtered), [filtered]);

    const addField = (def) => {
        if (selectedMap.has(def.key)) return;

        const labelI18n = ensureI18n(def.labels, langs);

        const newItem = {
            key: def.key,
            type: def.type,
            required: !!def.validations?.required,
            order: value.length,
            label: labelI18n,
            description: ensureI18n(def.descriptions, langs),
            placeholder: { ...labelI18n },
            options: def.options || [],
            dynamicOptionsToken: def.dynamicOptionsToken || '',
            conditionalDisplay: hasConditionalDisplay(def)
                ? def.conditionalDisplay
                : undefined,
            subdomains: Array.isArray(def.subdomains)
                ? def.subdomains.map((value) => String(value)).filter(Boolean)
                : [],
        };

        onChange?.([...value, newItem]);
    };

    const removeField = (key) =>
        onChange?.(value.filter((f) => f.key !== key).map((f, i) => ({ ...f, order: i })));

    const updateField = (key, patch) =>
        onChange?.(value.map((f) => (f.key === key ? { ...f, ...patch } : f)));

    const toggleFieldSubdomain = (fieldKey, subdomainValue) => {
        const currentField = selectedMap.get(fieldKey);
        const current = Array.isArray(currentField?.subdomains) ? currentField.subdomains : [];
        const exists = current.includes(subdomainValue);

        updateField(fieldKey, {
            subdomains: exists
                ? current.filter((value) => value !== subdomainValue)
                : [...current, subdomainValue],
        });
    };

    const setFieldAllSubdomains = (fieldKey) => {
        updateField(fieldKey, { subdomains: [] });
    };

    const updateConditionalDisplay = (key, patch) => {
        const current = selectedMap.get(key);

        updateField(key, {
            conditionalDisplay: {
                fieldKey: '',
                operator: 'equals',
                value: '',
                ...(current?.conditionalDisplay || {}),
                ...patch,
            },
        });
    };

    const enableConditionalDisplay = (key) => {
        updateField(key, {
            conditionalDisplay: {
                fieldKey: '',
                operator: 'equals',
                value: '',
            },
        });
    };

    const disableConditionalDisplay = (key) => {
        updateField(key, {
            conditionalDisplay: undefined,
        });
    };

    const move = (key, dir) => {
        const i = value.findIndex((f) => f.key === key);
        const j = i + dir;

        if (i < 0 || j < 0 || j >= value.length) return;

        const next = [...value];
        [next[i], next[j]] = [next[j], next[i]];

        onChange?.(next.map((f, k) => ({ ...f, order: k })));
    };

    const getFieldLabel = (key) => {
        const fromCatalog = selectedCatalogMap.get(key);
        const fromSelected = selectedMap.get(key);

        return (
            pickI18n(fromCatalog?.labels, lang) ||
            pickI18n(fromSelected?.label, lang) ||
            key
        );
    };

    const subdomainOptions = [
        { value: NO_SUBDOMAIN_KEY, label: 'Sans sous-domaine / domaine principal' },
        ...(subdomains || [])
            .map((subdomain) => ({
                value: getSubdomainValue(subdomain),
                label: getSubdomainLabel(subdomain),
            }))
            .filter((item) => item.value),
    ];

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-gray-600">
                    Sélectionnés : <span className="font-semibold">{value.length}</span>
                </div>

                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Rechercher un champ…"
                    className="border rounded px-3 py-2 w-full sm:w-80"
                    disabled={disabled}
                />
            </div>

            <div className="space-y-6">
                {Object.entries(grouped).map(([cat, fields]) => (
                    <div key={cat} className="space-y-3">
                        <div className="uppercase text-xs tracking-wider text-gray-500">{cat}</div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {fields.map((def) => {
                                const sel = selectedMap.get(def.key);
                                const isChecked = !!sel;

                                return (
                                    <div
                                        key={def._id || def.key}
                                        className={`rounded-xl border p-4 bg-white shadow-sm ${isChecked ? 'border-blue-400' : 'border-gray-200'
                                            }`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <label className="flex items-center gap-2 select-none">
                                                <input
                                                    type="checkbox"
                                                    className="h-4 w-4"
                                                    disabled={disabled}
                                                    checked={isChecked}
                                                    onChange={(e) =>
                                                        e.target.checked ? addField(def) : removeField(def.key)
                                                    }
                                                />

                                                <div>
                                                    <div className="font-medium text-gray-800">
                                                        {pickI18n(def.labels, lang)}{' '}
                                                        <span className="text-xs text-gray-400">({def.key})</span>
                                                    </div>

                                                    <div className="text-xs text-gray-500">
                                                        {pickI18n(def.descriptions, lang)}
                                                    </div>

                                                    {hasConditionalDisplay(def) && (
                                                        <div className="mt-1 inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700">
                                                            Condition globale
                                                        </div>
                                                    )}
                                                </div>
                                            </label>

                                            {isChecked && (
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        className="px-2 py-1 text-xs rounded border hover:bg-gray-50"
                                                        onClick={() => move(def.key, -1)}
                                                        disabled={disabled}
                                                    >
                                                        ↑
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="px-2 py-1 text-xs rounded border hover:bg-gray-50"
                                                        onClick={() => move(def.key, +1)}
                                                        disabled={disabled}
                                                    >
                                                        ↓
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {isChecked && (
                                            <div className="mt-3 grid grid-cols-1 gap-3">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-xs text-gray-600 mb-1">
                                                            Libellé ({lang.toUpperCase()})
                                                        </label>

                                                        <input
                                                            type="text"
                                                            className="w-full border rounded px-3 py-2"
                                                            value={ensureI18n(sel.label, langs)[lang]}
                                                            onChange={(e) =>
                                                                updateField(def.key, {
                                                                    label: {
                                                                        ...ensureI18n(sel.label, langs),
                                                                        [lang]: e.target.value,
                                                                    },
                                                                })
                                                            }
                                                            disabled={disabled}
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs text-gray-600 mb-1">
                                                            Placeholder ({lang.toUpperCase()})
                                                        </label>

                                                        <input
                                                            type="text"
                                                            className="w-full border rounded px-3 py-2"
                                                            value={ensureI18n(sel.placeholder, langs)[lang]}
                                                            onChange={(e) =>
                                                                updateField(def.key, {
                                                                    placeholder: {
                                                                        ...ensureI18n(sel.placeholder, langs),
                                                                        [lang]: e.target.value,
                                                                    },
                                                                })
                                                            }
                                                            disabled={disabled}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-xs text-gray-600 mb-1">
                                                        Description d'aide ({lang.toUpperCase()})
                                                    </label>

                                                    <textarea
                                                        rows={2}
                                                        className="w-full border rounded px-3 py-2"
                                                        value={ensureI18n(sel.description, langs)[lang]}
                                                        onChange={(e) =>
                                                            updateField(def.key, {
                                                                description: {
                                                                    ...ensureI18n(sel.description, langs),
                                                                    [lang]: e.target.value,
                                                                },
                                                            })
                                                        }
                                                        disabled={disabled}
                                                    />

                                                    <p className="text-[11px] text-gray-500 mt-1">
                                                        S'affichera sous le champ côté utilisateur pour expliquer ce qui est attendu.
                                                    </p>
                                                </div>

                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            id={`req-${def.key}`}
                                                            type="checkbox"
                                                            className="h-4 w-4"
                                                            checked={!!sel.required}
                                                            onChange={(e) => updateField(def.key, { required: e.target.checked })}
                                                            disabled={disabled}
                                                        />

                                                        <label
                                                            htmlFor={`req-${def.key}`}
                                                            className="text-sm text-gray-700 select-none"
                                                        >
                                                            Champ requis
                                                        </label>
                                                    </div>

                                                    <div className="text-xs text-gray-500">Type : {def.type}</div>
                                                </div>

                                                {Array.isArray(subdomains) && (
                                                    <details
                                                        className="rounded-xl border bg-white mt-1 group"
                                                        defaultOpen={(sel.subdomains || []).length > 0}
                                                    >
                                                        <summary className="flex items-center justify-between gap-3 p-3 cursor-pointer list-none">
                                                            <div className="flex items-center gap-3">
                                                                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border bg-gray-50 text-gray-600 transition-transform group-open:rotate-90">
                                                                    ›
                                                                </span>

                                                                <div>
                                                                    <h4 className="text-sm font-semibold text-gray-800">
                                                                        Affichage selon le sous-domaine
                                                                    </h4>
                                                                    <p className="text-xs text-gray-500">
                                                                        Si aucune option n’est sélectionnée, le champ sera affiché partout.
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        setFieldAllSubdomains(def.key);
                                                                    }}
                                                                    className={`text-xs rounded px-2 py-1 border ${(sel.subdomains || []).length === 0
                                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                                        }`}
                                                                    disabled={disabled}
                                                                >
                                                                    Tous
                                                                </button>

                                                                <span className="text-xs text-gray-500 whitespace-nowrap">
                                                                    {(sel.subdomains || []).length} sélectionné(s)
                                                                </span>
                                                            </div>
                                                        </summary>

                                                        <div className="px-3 pb-3">
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-44 overflow-y-auto pr-1">
                                                                {subdomainOptions.map((option) => {
                                                                    const checked = (sel.subdomains || []).includes(option.value);

                                                                    return (
                                                                        <label
                                                                            key={option.value}
                                                                            className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 cursor-pointer ${checked
                                                                                ? 'bg-blue-50 border-blue-400 text-blue-800'
                                                                                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                                                                }`}
                                                                        >
                                                                            <div className="min-w-0">
                                                                                <div className="text-sm font-medium truncate">
                                                                                    {option.label}
                                                                                </div>
                                                                                {option.value !== NO_SUBDOMAIN_KEY && (
                                                                                    <div className="text-xs text-gray-500 truncate">
                                                                                        {option.value}
                                                                                    </div>
                                                                                )}
                                                                            </div>

                                                                            <input
                                                                                type="checkbox"
                                                                                checked={checked}
                                                                                onChange={() => toggleFieldSubdomain(def.key, option.value)}
                                                                                disabled={disabled}
                                                                                className="h-4 w-4"
                                                                            />
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>

                                                            {(sel.subdomains || []).length > 0 && (
                                                                <div className="mt-3 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-700">
                                                                    Ce champ sera affiché uniquement pour les emplacements sélectionnés.
                                                                </div>
                                                            )}
                                                        </div>
                                                    </details>
                                                )}

                                                <div className="rounded-xl border bg-gray-50 p-3">
                                                    <div className="flex items-center justify-between gap-3 mb-3">
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-700">
                                                                Affichage conditionnel
                                                            </div>

                                                            <p className="text-xs text-gray-500">
                                                                Surcharge possible pour ce service.
                                                            </p>
                                                        </div>

                                                        <label className="inline-flex items-center gap-2 text-sm">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!sel.conditionalDisplay?.fieldKey}
                                                                onChange={(e) =>
                                                                    e.target.checked
                                                                        ? enableConditionalDisplay(def.key)
                                                                        : disableConditionalDisplay(def.key)
                                                                }
                                                                disabled={disabled}
                                                            />
                                                            Activer
                                                        </label>
                                                    </div>

                                                    {sel.conditionalDisplay && (
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                            <div>
                                                                <label className="text-xs text-gray-600">
                                                                    Champ déclencheur
                                                                </label>

                                                                <select
                                                                    value={sel.conditionalDisplay?.fieldKey || ''}
                                                                    onChange={(e) =>
                                                                        updateConditionalDisplay(def.key, {
                                                                            fieldKey: e.target.value,
                                                                        })
                                                                    }
                                                                    className="w-full border rounded px-3 py-2 text-sm"
                                                                    disabled={disabled}
                                                                >
                                                                    <option value="">— Choisir —</option>

                                                                    {(value || [])
                                                                        .filter((f) => f.key !== def.key)
                                                                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                                                                        .map((f) => (
                                                                            <option key={f.key} value={f.key}>
                                                                                {f.key} — {getFieldLabel(f.key)}
                                                                            </option>
                                                                        ))}
                                                                </select>
                                                            </div>

                                                            <div>
                                                                <label className="text-xs text-gray-600">
                                                                    Condition
                                                                </label>

                                                                <select
                                                                    value={sel.conditionalDisplay?.operator || 'equals'}
                                                                    onChange={(e) =>
                                                                        updateConditionalDisplay(def.key, {
                                                                            operator: e.target.value,
                                                                        })
                                                                    }
                                                                    className="w-full border rounded px-3 py-2 text-sm"
                                                                    disabled={disabled}
                                                                >
                                                                    <option value="equals">Est égal à</option>
                                                                    <option value="not_equals">Est différent de</option>
                                                                </select>
                                                            </div>

                                                            <div>
                                                                <label className="text-xs text-gray-600">
                                                                    Valeur attendue
                                                                </label>

                                                                <input
                                                                    value={sel.conditionalDisplay?.value || ''}
                                                                    onChange={(e) =>
                                                                        updateConditionalDisplay(def.key, {
                                                                            value: e.target.value,
                                                                        })
                                                                    }
                                                                    className="w-full border rounded px-3 py-2 text-sm"
                                                                    placeholder="ex: Oui"
                                                                    disabled={disabled}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}

                                                    {sel.conditionalDisplay?.fieldKey && (
                                                        <div className="mt-3 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-700">
                                                            Ce champ sera affiché si{' '}
                                                            <strong>{getFieldLabel(sel.conditionalDisplay.fieldKey)}</strong>{' '}
                                                            {sel.conditionalDisplay.operator === 'equals'
                                                                ? 'est égal à'
                                                                : 'est différent de'}{' '}
                                                            <strong>{String(sel.conditionalDisplay.value || '')}</strong>.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {value.length > 0 && (
                <div className="rounded-xl border bg-blue-50 text-blue-900 px-4 py-3 text-sm">
                    Astuce : l'ordre affiché ci-dessus sera l'ordre du formulaire côté utilisateur.
                </div>
            )}
        </div>
    );
}